import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Popover, Radio, Segmented, Select, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { trainingAPI } from "@api/TrainingAPI";
import {
  countSourceFromMetricId as countSourceFromMetricIdRaw,
  defaultCountSpec as defaultCountSpecRaw,
} from "../../Processing/countSpec.mjs";

/**
 * One in-flight request shared by every picker on the page.
 *
 * The catalogue is team-scoped and changes rarely, but a table with five count columns
 * mounts five pickers, and each would otherwise issue its own identical request. The
 * promise is memoised rather than the result, so five simultaneous mounts share one
 * request rather than racing five.
 */
let countOptionsRequest: Promise<CountOptions | null> | null = null;

function fetchCountOptions(): Promise<CountOptions | null> {
  if (!countOptionsRequest) {
    countOptionsRequest = trainingAPI
      .getCountOptions()
      .then((data: any) => (data ?? null) as CountOptions | null)
      .catch(() => {
        // Let the next picker retry rather than caching a failure for the session.
        countOptionsRequest = null;
        return null;
      });
  }
  return countOptionsRequest;
}

/** `count-flags` -> `flags`. Null for any measured metric id. */
export function countSourceFromMetricId(metricId: string | null | undefined): CountSource | null {
  return countSourceFromMetricIdRaw(metricId) as CountSource | null;
}

export function defaultCountSpec(source: CountSource): CountSpec {
  return defaultCountSpecRaw(source) as CountSpec;
}

const { Text } = Typography;

/** The four sources, keyed by the metric id the dropdown carries (`count-<source>`). */
export type CountSource = "flags" | "statuses" | "injuries" | "treatments";

export interface CountSpec {
  source: CountSource;
  /** `"<facet>:<value>"` entries. Empty counts everything the source offers. */
  include: string[];
  /** How the facets combine. Only meaningful on a source with two or more. */
  match: "all" | "any";
  /** Injuries only; left out for every other source. */
  reading?: "incidence" | "prevalence";
}

interface FacetOption {
  value: string;
  label: string;
  color?: string;
  group?: string;
  retired?: boolean;
}

type CountOptions = Record<string, Record<string, FacetOption[]>>;

/** The label a facet key reads as in the picker. */
const FACET_LABELS: Record<string, string> = {
  flag: "Flag",
  status: "Status",
  type: "Type",
  location: "Location",
  soft_tissue: "Soft Tissue",
  treatment: "Treatment",
  application_time: "Application Time",
};

// An empty selection counts every option, so the placeholder reads "All flags", not
// "All flag". Held as its own map because the plurals are not all a trailing "s".
const FACET_ALL_LABELS: Record<string, string> = {
  flag: "All flags",
  status: "All statuses",
  type: "All types",
  location: "All locations",
  soft_tissue: "Any soft tissue",
  treatment: "All treatments",
  application_time: "All application times",
};

export const COUNT_SOURCE_LABELS: Record<CountSource, string> = {
  flags: "Flags",
  statuses: "Statuses",
  injuries: "Injuries",
  treatments: "Treatments",
};

/** The backend rejects `match: "any"` on a single-facet source, where the two modes
 *  select the same rows. The control is hidden there rather than shown inert. */
function facetCount(options: CountOptions, source: CountSource): number {
  return Object.keys(options?.[source] ?? {}).length;
}


/** A count always totals its rows; there is no average or best to choose. */
export function isCountMetricId(metricId: string | null | undefined): boolean {
  return countSourceFromMetricId(metricId) !== null;
}


/**
 * The one-line summary the configurator shows, e.g. "GPS Overworked +2".
 * Reads "All flags" until the author checks something.
 */
export function countSummaryLabel(
  spec: CountSpec | undefined,
  options: CountOptions | null,
): string {
  if (!spec) return "";
  const source = spec.source;
  if (spec.include.length === 0) return `All ${COUNT_SOURCE_LABELS[source].toLowerCase()}`;
  const labelFor = (entry: string) => {
    for (const facet of Object.values(options?.[source] ?? {})) {
      const hit = facet.find((o) => o.value === entry);
      if (hit) return hit.label;
    }
    return entry.split(":").slice(1).join(":");
  };
  const first = labelFor(spec.include[0]);
  const rest = spec.include.length - 1;
  const suffix = rest > 0 ? ` +${rest}` : "";
  // Only the non-default mode is marked: two widgets differing only in mode must not
  // read the same, and `all` is the default.
  const mode = spec.match === "any" && spec.include.length > 1 ? " (any)" : "";
  return `${first}${suffix}${mode}`;
}

/**
 * The count entries as `MetricOption`s, for the widgets that pick metrics through
 * `MetricPicker` rather than the grouped dropdown (gauge and stat tile).
 *
 * Deliberately not added to `useDataPipeline.getMetrics()`: that list also feeds
 * `resolveSpecBaselines`, and a count has no baseline. Appending here keeps the two
 * widgets that want counts from widening a list three other callers read.
 */
export function useCountMetricOptions(): Array<{ id: string; name: string; category: string }> {
  const allMetricTypes: any = useSelector(({ team }: any) => team.all_metric_types);
  return useMemo(() => {
    // The backend is the gate: it returns count entries only for a team the rollout has
    // reached, so an empty list means this team has no counts.
    return (allMetricTypes?.count ?? []).map((m: any) => ({
      id: m.unique_identifier as string,
      name: m.name as string,
      category: "Counts",
    }));
  }, [allMetricTypes]);
}

interface CountIncludePickerProps {
  spec: CountSpec;
  onChange: (spec: CountSpec) => void;
}

/**
 * The popover that says which items a count tallies.
 *
 * Entries of one facet always combine with OR. The Match control decides how the
 * facets combine, and only appears on a source that has more than one.
 */
export function CountIncludePicker({ spec, onChange }: CountIncludePickerProps) {
  const [options, setOptions] = useState<CountOptions | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCountOptions().then((data) => {
      if (!cancelled) setOptions(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const facets = options?.[spec.source] ?? {};
  const checked = useMemo(() => new Set(spec.include), [spec.include]);
  const multiFacet = facetCount(options ?? {}, spec.source) > 1;

  // An entry whose option the catalogue no longer offers — a user-defined flag deleted
  // since this widget was built. It rides in its own facet's control as a "Removed"
  // option, so the author can see its name and take it off. Without this the control
  // shows a bare id it cannot explain. Only computed once the catalogue has loaded, so
  // a pending fetch does not orphan everything.
  const orphanedByFacet = useMemo(() => {
    const out: Record<string, string[]> = {};
    if (!options) return out;
    const known = new Set(
      Object.values(options[spec.source] ?? {}).flatMap((f) => f.map((o) => o.value)),
    );
    for (const entry of spec.include) {
      if (known.has(entry)) continue;
      const facetKey = entry.slice(0, entry.indexOf(":"));
      (out[facetKey] ||= []).push(entry);
    }
    return out;
  }, [options, spec.source, spec.include]);

  /** Replace one facet's selection, leaving every other facet's entries alone. */
  const replaceFacet = (facetKey: string, selected: string[]) => {
    const ownValues = new Set((facets[facetKey] ?? []).map((o) => o.value));
    const others = spec.include.filter((entry) => !ownValues.has(entry));
    onChange({ ...spec, include: [...others, ...selected].sort() });
  };

  /** One facet's multi-select. The same control the tracker's flag filter uses. */
  const facetSelect = (facetKey: string) => {
    const facetOptions = facets[facetKey] ?? [];
    const orphans = orphanedByFacet[facetKey] ?? [];
    const label = FACET_LABELS[facetKey] ?? facetKey;
    return (
      <Select
        size="small"
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        maxTagCount={1}
        // flex/minWidth so the control fills whichever row holds it, including the
        // Table2D one, which is a plain flex div rather than .awm-labeled-row.
        style={{ width: "100%", flex: 1, minWidth: 0 }}
        placeholder={FACET_ALL_LABELS[facetKey] ?? `All ${label.toLowerCase()}`}
        value={[...facetOptions.filter((o) => checked.has(o.value)).map((o) => o.value),
                ...orphans]}
        onChange={(next: string[]) => replaceFacet(facetKey, next)}
        options={[
          ...facetOptions.map((o) => ({
            value: o.value,
            label: o.label,
            // A retired option stays selectable while it is already selected, so an
            // existing widget keeps its filter and the author can take it off.
            disabled: o.retired && !checked.has(o.value),
            color: o.color,
            note: o.retired ? "Removed" : undefined,
          })),
          // Deleted since this widget was built. Its past rows are still counted, so
          // it shows by name and can be removed like any other entry.
          ...orphans.map((entry) => ({
            value: entry,
            label: entry.slice(entry.indexOf(":") + 1) || entry,
            disabled: false,
            color: undefined,
            note: "No longer available",
          })),
        ]}
        optionRender={(option: any) => (
          <span>
            {option.data.color && (
              <span className="awm-count-swatch" style={{ background: option.data.color }} />
            )}
            {option.data.label}
            {option.data.note && (
              <Text type="secondary" style={{ fontSize: 10, marginLeft: 6 }}>
                {option.data.note}
              </Text>
            )}
          </span>
        )}
      />
    );
  };

  const facetKeys = Object.keys(facets);

  // One filter and nothing else to set — flags and statuses — so the control goes
  // straight in the row, like every other field in the configurator. A popover there
  // hides a single dropdown behind a click for no reason.
  if (!multiFacet) {
    return facetKeys.length === 0 ? (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No options available for this team yet.
      </Text>
    ) : (
      facetSelect(facetKeys[0])
    );
  }

  // Several facets, plus a Match mode and (for injuries) a reading: more than a row
  // holds, so these stay in a popover.
  const body = (
    <div style={{ width: 300 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Text strong style={{ fontSize: 13 }}>
          {COUNT_SOURCE_LABELS[spec.source]} to count
        </Text>
        <Button type="link" size="small" style={{ padding: 0 }}
          onClick={() => onChange({ ...spec, include: [] })}>
          Clear
        </Button>
      </div>

      {spec.source === "injuries" && (
        <div style={{ marginTop: 10 }}>
          <div className="awm-count-facet-name">Count</div>
          <Radio.Group
            size="small"
            value={spec.reading ?? "incidence"}
            onChange={(e) => onChange({ ...spec, reading: e.target.value })}
          >
            <Radio value="incidence">New injuries</Radio>
            <Radio value="prevalence">Open injuries</Radio>
          </Radio.Group>
          {spec.reading === "prevalence" && (
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
              An open injury counts in every bucket its spell runs through, so the
              buckets do not add up to the range total.
            </Text>
          )}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <div className="awm-count-facet-name">Match</div>
        {/* `block` makes the control fit the popover instead of running past its
            edge, and the labels stay short enough not to squeeze at 300px. */}
        <Segmented
          block
          size="small"
          value={spec.match}
          onChange={(v) => onChange({ ...spec, match: v as "all" | "any" })}
          options={[
            { label: "All conditions", value: "all" },
            { label: "Any conditions", value: "any" },
          ]}
        />
      </div>

      <div style={{ maxHeight: 280, overflowY: "auto", marginTop: 10 }}>
        {facetKeys.map((facetKey) => (
          <div key={facetKey} style={{ marginBottom: 10 }}>
            <div className="awm-count-facet-name">{FACET_LABELS[facetKey] ?? facetKey}</div>
            {facetSelect(facetKey)}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="rightTop"
      content={body}
    >
      <div className="awm-custom-range-summary" role="button" tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen(!open);
        }}>
        <span>{countSummaryLabel(spec, options)}</span>
        <EditOutlined style={{ fontSize: 11 }} />
      </div>
    </Popover>
  );
}
